/**
 * Professional ART Service
 * Cliente HTTP das ARTs de um profissional.
 *
 * Todas as rotas são aninhadas em profissionais (`/professionals/:id/arts`) —
 * não existe rota de ART na raiz da API.
 */
import apiClient from './apiClient';
import type { ApiResponse } from '../types/common';
import type {
  ProfessionalArt,
  ProfessionalArtInput,
  ProfessionalArtUpdate,
} from '../types/professionalArt';

// Re-export types for convenience
export * from '../types/professionalArt';

/**
 * Forma crua que a API devolve. `id`, `professionalId` e `companyId` são
 * BIGINT no MariaDB e o Sequelize os serializa como **string** na maior parte
 * das respostas (medido: GET /professionals/3/arts → `{"id":"3", ...}`).
 * `originalName` simplesmente não vem quando a ART não tem anexo.
 */
interface ProfessionalArtRaw {
  id: number | string;
  professionalId: number | string;
  companyId: number | string;
  companyName?: string;
  artNumber: string;
  issueDate: string;
  expiryDate?: string | null;
  originalName?: string | null;
  notes?: string | null;
}

/**
 * Traz a resposta para o tipo do frontend. Sem isto, `art.companyId === company.id`
 * na tela compara `'1'` com `1` e nunca casa.
 */
const normalizar = (raw: ProfessionalArtRaw): ProfessionalArt => ({
  id: Number(raw.id),
  professionalId: Number(raw.professionalId),
  companyId: Number(raw.companyId),
  ...(raw.companyName === undefined ? {} : { companyName: raw.companyName }),
  artNumber: raw.artNumber,
  issueDate: raw.issueDate.slice(0, 10),
  expiryDate: raw.expiryDate ?? null,
  originalName: raw.originalName ?? null,
  notes: raw.notes ?? null,
});

function unwrap<T>(body: ApiResponse<T>, erro: string): T {
  if (body.data === undefined) {
    throw new Error(erro);
  }
  return body.data;
}

class ProfessionalArtService {
  private baseUrl(professionalId: string | number): string {
    return `/professionals/${professionalId}/arts`;
  }

  /** ARTs do profissional, já filtradas pelo escopo de tenant do solicitante. */
  async list(professionalId: string | number): Promise<ProfessionalArt[]> {
    const body = await apiClient.get<ProfessionalArtRaw[]>(this.baseUrl(professionalId));
    return (body.data ?? []).map(normalizar);
  }

  async create(
    professionalId: string | number,
    data: ProfessionalArtInput
  ): Promise<ProfessionalArt> {
    const body = await apiClient.post<ProfessionalArtRaw>(this.baseUrl(professionalId), data);
    return normalizar(unwrap(body, 'Resposta sem dados da ART criada'));
  }

  /**
   * Atualiza a ART. O corpo NÃO leva `companyId` nem `professionalId`: o
   * backend recusa a troca de qualquer um dos dois com ValidationError.
   */
  async update(
    professionalId: string | number,
    artId: string | number,
    data: ProfessionalArtUpdate
  ): Promise<ProfessionalArt> {
    const body = await apiClient.put<ProfessionalArtRaw>(
      `${this.baseUrl(professionalId)}/${artId}`,
      data
    );
    return normalizar(unwrap(body, 'Resposta sem dados da ART atualizada'));
  }

  async remove(professionalId: string | number, artId: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl(professionalId)}/${artId}`);
  }

  async uploadFile(
    professionalId: string | number,
    artId: string | number,
    file: File
  ): Promise<ProfessionalArt> {
    const formData = new FormData();
    formData.append('file', file);
    const body = await apiClient.post<ProfessionalArtRaw>(
      `${this.baseUrl(professionalId)}/${artId}/file`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return normalizar(unwrap(body, 'Resposta sem dados da ART após o upload'));
  }

  /**
   * Remove SÓ o anexo, preservando a ART — número, emissão e validade
   * continuam. Sem isto, trocar um PDF errado obrigava a excluir a ART inteira
   * e recadastrá-la.
   */
  async removeFile(
    professionalId: string | number,
    artId: string | number
  ): Promise<ProfessionalArt> {
    const body = await apiClient.delete<ProfessionalArtRaw>(
      `${this.baseUrl(professionalId)}/${artId}/file`
    );
    return normalizar(unwrap(body, 'Resposta sem dados da ART após remover o anexo'));
  }

  /**
   * Baixa o anexo. Passa pelo cliente autenticado porque a rota exige Bearer
   * token — um `<a href>` cru devolveria 401.
   */
  async downloadFile(
    professionalId: string | number,
    artId: string | number,
    fileName?: string
  ): Promise<void> {
    await apiClient.downloadFile(`${this.baseUrl(professionalId)}/${artId}/file`, fileName);
  }
}

export const professionalArtService = new ProfessionalArtService();
export default professionalArtService;
